/**
 * Servicio principal para Contentful usando fetch nativo
 * Reemplaza Apollo Client con fetch nativo para mejor compatibilidad
 */

const CONTENTFUL_ENDPOINT = process.env.REACT_APP_API_GRAPH as string
const CONTENTFUL_ACCESS_TOKEN =
	process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN || 'your_access_token_here'

interface GraphQLRequest {
	query: string
	variables?: Record<string, any>
}

interface GraphQLResponse<T = any> {
	data?: T
	errors?: Array<{
		message: string
		locations?: Array<{ line: number; column: number }>
		path?: string[]
	}>
}

/**
 * Cliente GraphQL usando fetch nativo
 */
class ContentfulClient {
	private endpoint: string
	private accessToken: string

	constructor() {
		this.endpoint = CONTENTFUL_ENDPOINT
		this.accessToken = CONTENTFUL_ACCESS_TOKEN
	}

	/**
	 * Ejecuta una query GraphQL
	 */
	async request<T = any>({ query, variables }: GraphQLRequest): Promise<T> {
		try {
			const response = await fetch(this.endpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${this.accessToken}`
				},
				body: JSON.stringify({
					query,
					variables
				})
			})

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`)
			}

			const result: GraphQLResponse<T> = await response.json()

			if (result.errors && result.errors.length > 0) {
				throw new Error(`GraphQL errors: ${result.errors.map(e => e.message).join(', ')}`)
			}

			return result.data as T
		} catch (error) {
			console.error('Contentful request failed:', error)
			throw error
		}
	}

	/**
	 * Ejecuta una query con retry automático
	 */
	async requestWithRetry<T = any>(request: GraphQLRequest, maxRetries = 2): Promise<T> {
		let lastError: Error | null = null

		for (let attempt = 0; attempt <= maxRetries; attempt++) {
			try {
				return await this.request<T>(request)
			} catch (error) {
				lastError = error as Error

				if (attempt === maxRetries) {
					break
				}

				// Backoff exponencial
				const delay = Math.min(1000 * Math.pow(2, attempt), 30000)
				await new Promise(resolve => setTimeout(resolve, delay))
			}
		}

		throw lastError || new Error('Request failed after all retries')
	}
}

// Instancia singleton del cliente
export const contentfulClient = new ContentfulClient()

/**
 * Hook para usar el cliente de Contentful
 */
export const useContentfulClient = () => {
	return contentfulClient
}

// Query para Premium
const PREMIUM_QUERY = `
	query contenido {
		homeCollection(limit: 1, where: { titulo: "Salud continua" }) {
			items {
				estadosPremium {
					referenciaContenidoCollection(limit: 10) {
						items {
							tituloGeneral
							tituloBoton
							descripcion {
								json
							}
							tituloDescripcion
							id
							imagenPrincipal {
								url
							}
							redireccionBoton
						}
					}
				}
			}
		}
	}
`

// Query para Your Condition
const YOUR_CONDITION_QUERY = `
	query contenido {
		homeCollection(limit: 1, where: { titulo: "Salud continua" }) {
			items {
				vistas {
					tuCondicion {
						beneficioCollection(limit: 15) {
							items {
								titulo
								idReto
								tituloGeneral
								descripcionGeneral
								tituloCard
								descripcionCard
								titulo2
								descripcion2
								titulo3
								textoBoton
								isVisible
								imagen {
									url
								}
								beneficioCollection(limit: 15) {
									items {
										idReto
										tituloMagenta
										tituloGeneral
										tituloCard
										titulo2
										titulo3
										link
										descripcionGeneral
										descripcion2
										descripcion3
										textoBoton
										color
										isVisible
										imagen {
											url
										}
										beneficioCollection(limit: 15) {
											items {
												idReto
												tituloGeneral
												titulo2
												titulo3
												tituloCard
												descripcionGeneral
												descripcion3
												descripcion2
												link
												isVisible
												imagen {
													url
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}
`

// Query para Your Habits
const YOUR_HABITS_QUERY = `
	query contenido {
		homeCollection(limit: 1, where: { titulo: "Salud continua" }) {
			items {
				vistas {
					contenidoEntrenaTusHabitos {
						idReto
						beneficioCollection(limit: 10) {
							items {
								idReto
								tituloGeneral
								descripcionGeneral
								tituloCard
								imagen {
									url
								}
								textoBoton
								link
								isVisible
								descripcion3
								beneficioCollection(limit: 10) {
									items {
										tituloGeneral
										descripcionGeneral
										tituloCard
										imagen {
											url
										}
										textoBoton
										link
										descripcion3
										isVisible
									}
								}
							}
						}
					}
				}
			}
		}
	}
`

/**
 * API para Contentful
 */
export const contentfulApi = {
	/**
	 * Obtiene los datos de Premium
	 */
	getPremium: async () => {
		return await contentfulClient.request<any>({
			query: PREMIUM_QUERY
		})
	},

	/**
	 * Obtiene los datos de Your Condition
	 */
	getYourCondition: async () => {
		return await contentfulClient.request<any>({
			query: YOUR_CONDITION_QUERY
		})
	},

	/**
	 * Obtiene los datos de Your Habits
	 */
	getYourHabits: async () => {
		return await contentfulClient.request<import('../types/contentful.types').YourHabitsResponse>({
			query: YOUR_HABITS_QUERY
		})
	}
}

/**
 * API para Your Habits (mantener compatibilidad)
 */
export const yourHabitsApi = {
	/**
	 * Obtiene los datos de Your Habits
	 */
	getYourHabits: async () => {
		return await contentfulClient.request<import('../types/contentful.types').YourHabitsResponse>({
			query: YOUR_HABITS_QUERY
		})
	}
}

export default contentfulClient
